import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GWP_CHART_OPTIOINS, HOTSPOT_CHART_OPTIOINS } from '../../utils/chart-options';
import { HighchartsChartModule } from 'highcharts-angular';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ButtonComponent, ButtonToggleComponent, LoaderModule } from '@mds/ui';
import Highcharts from 'highcharts';
import { EmptyChartComponent } from '@pages/dashboards/shared/empty-chart/empty-chart.component';
import { PefAnalysisService } from '@pages/pef-analysis/services/pef-anlysis.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { ToastrService } from '@services/toaster.service';

@Component({
  selector: 'app-gwp-chart',
  standalone: true,
  imports: [
    CommonModule,
    MdsIconsModule,
    HighchartsChartModule,
    LoaderModule,
    EmptyChartComponent
  ],
  templateUrl: './gwp-chart.component.html',
  styleUrls: ['./gwp-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GwpChartComponent implements OnInit {
  protected readonly Highcharts = Highcharts;
  @Input() rid: string;
  chartOptions: Highcharts.Options;
  hotspotChartOptions: Highcharts.Options;
  gwpLoading: boolean = false;
  hotspotsLoading: boolean = false;
  icons = MdsIcon;
  donutChartData: any;
  pieChartData: any;
  emptyChart = { title: 'No Data', description: 'No data available' };
  isEmptyGWP: boolean = false;
  isEmptyHotspot: boolean = false;
  private destroyRef = inject(DestroyRef);
  private pefAnalysisService = inject(PefAnalysisService);
  private cdr = inject(ChangeDetectorRef);
  private toastrService = inject(ToastrService);
  ngOnInit(): void {
    this.getGWP();
    this.getHotspots();
  }

  private getGWP() {
    if (!this.rid) return;
    this.gwpLoading = true;
    this.pefAnalysisService
      .getLCBreakdowns(this.rid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.gwpLoading = false))
      )
      .subscribe({
        next: (res: { lines: any[]; name: string; unit: string }) => {

          this.donutChartData = res;
          let total = 0;
          if (res.lines.length < 1) {
            this.isEmptyGWP = true;
            return;
          }
          const lines = res.lines
            .filter((r, i) => i < 5)
            .map((d) => {
              total = total + parseFloat(d.impact);
              return {
                name: d.label,
                y: parseFloat(d.impact)
              };
            });
          this.updateGWPChart(lines, res.name, total, res.unit);
        },
        error: (err) => {
          const defaultPlaceholder = { name: 'GWP along the Product Life Cycle Stages', unit: 'kg CO2 eq' };
          this.donutChartData = defaultPlaceholder;
          this.isEmptyGWP = true;
          this.gwpLoading = false;
          this.toastrService.showMessage(err?.error?.message);
          this.cdr.markForCheck();
        }
      });
  }

  private updateGWPChart(data: any, name: string = '', value: number = 0, unit: string = '') {
    this.chartOptions = this.getGWPOptions(data, name, value, unit);
    this.cdr.markForCheck();
  }

  private getGWPOptions(data: any, chartName: string, value: number, unit: string) {
    const colors = ['#00747A', '#219491', '#01C9B3', '#80E4D9', '#BFF1EC'];
    return {
      ...GWP_CHART_OPTIOINS('Total', value, unit, colors),
      series: [{ type: 'pie', data }]
    } as Highcharts.Options;
  }

  private getHotspots() {
    if (!this.rid) return;
    this.hotspotsLoading = true;
    this.pefAnalysisService
      .getMaterialBreakdowns(this.rid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.hotspotsLoading = false))
      )
      .subscribe({
        next: (res: { lines: any[]; name: string; unit: string }) => {
          this.pieChartData = res;
          if (res.lines.length < 1) {
            this.isEmptyHotspot = true;
            return;
          }
          let total = 0;
          const lines = res.lines
            .filter((r, i) => i < 5)
            .map((d) => {
              total = total + parseFloat(d.impact);
              return {
                name: d.label,
                y: parseFloat(d.impact)
              };
            });
          this.updateHotspotChart(lines, res.name, total, res.unit);
          this.cdr.markForCheck();
        },
        error: (err) => {
          const defaultPlaceholder = { name: 'Major Raw Materials Contribution', unit: 'kg CO2 eq' };
          this.pieChartData = defaultPlaceholder;
          this.isEmptyHotspot = true;
          this.hotspotsLoading = false;
          this.toastrService.showMessage(err?.error?.message);
          this.cdr.markForCheck();
        }
      });
  }

  private updateHotspotChart(data: any, name: string = '', value: number = 0, unit: string = '') {
    this.hotspotChartOptions = this.getHotspotOptions(data, name, value, unit);
    this.cdr.markForCheck();
  }

  private getHotspotOptions(data, chartName: string, value: number, unit: string) {
    const colors = ['#00747A', '#219491', '#01C9B3', '#80E4D9', '#BFF1EC'];
    return {
      ...HOTSPOT_CHART_OPTIOINS('Total', value, unit, colors),
      series: [{ type: 'pie', data }]
    } as Highcharts.Options;
  }
}
